import { ExportQueueStatus } from 'api/async-export';
import { TaskAPI } from 'api/tasks';
import type { Filter } from 'components/molecules/filters/types';
import { toastService } from 'components/molecules/toast';
import type { ColumnOrder } from 'components/organisms/grid-custom-column';
import { FieldFilter } from 'components/organisms/tasks/filters';
import type { Translate } from 'hooks/use-translation';
import type { TaskField } from 'stores/fields';
import { getFieldString } from 'utils/export';
import { validateTaskColumns } from '../export/utils';

interface Props {
  filter: Filter;
  columnOrder: ColumnOrder[];
  lookup: Record<string, TaskField>;
  failExport: () => void;
  translate: Translate;
  projectId: number;
}

const getValidFilter = (filter: Filter): FieldFilter[] =>
  filter.criteria.filter(FieldFilter.isValid);

const getValidColumnOrder = (
  columnOrder: ColumnOrder[],
  lookup: Record<string, TaskField>,
) => {
  return columnOrder
    .filter((x) => !x.hide)
    .map((x) => {
      const colId = x.colId === 'checkbox' ? 'taskName' : x.colId;

      if (colId) {
        const field = lookup[colId];

        if (field) {
          return getFieldString(field);
        }
      }

      return '';
    })
    .filter(Boolean);
};

export const exportAsCSV = ({
  filter,
  columnOrder,
  projectId,
  lookup,
  failExport,
  translate,
}: Props): void => {
  const validFilters = getValidFilter(filter);

  let validColumnOrder = getValidColumnOrder(columnOrder, lookup);

  if (validColumnOrder.length === 0) {
    failExport();

    toastService({
      title: translate('async.exports.error.title'),
      message: translate('async.exports.error.message'),
    }).error();

    return;
  }
  validColumnOrder = validateTaskColumns(validColumnOrder);

  const exportPromise = TaskAPI.exportAsCSVAsyncForNonGroup({
    filters: validFilters,
    match: filter.type,
    columnOrder: validColumnOrder,
    projectId,
  });

  exportPromise
    .then((res) => res.status)
    .then((status) => {
      if (status === ExportQueueStatus.Submitted) {
        toastService({
          title: translate('async.exports.in_progress.title'),
          message: translate('async.exports.in_progress.message'),
        }).success();
      }
    })
    .catch(() => {
      toastService({
        title: translate('async.exports.error.title'),
        message: translate('async.exports.error.message'),
      }).error();
    });
};
